import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { CustomerUserDTO } from '../../../models/customer.model';
import { AvatarComponent } from '../../avatar/avatar.component';
import { PersonOption, PersonSelectComponent } from '../../person-select/person-select.component';

export type ProjektRolle = 'PROJECT_ADMIN' | 'PROJECT_WRITER' | 'PROJECT_READER';

export interface TeamZeile {
  personId: string;
  rolle: ProjektRolle;
}

export const PROJEKT_ROLLEN: readonly { rolle: ProjektRolle; schluessel: string }[] = [
  { rolle: 'PROJECT_ADMIN', schluessel: 'leiter' },
  { rolle: 'PROJECT_WRITER', schluessel: 'tester' },
  { rolle: 'PROJECT_READER', schluessel: 'beobachter' },
];

@Component({
  selector: 'app-team-schritt',
  standalone: true,
  imports: [
    AvatarComponent,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    PersonSelectComponent,
    TranslatePipe,
  ],
  templateUrl: './team-schritt.component.html',
  styleUrl: './team-schritt.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamSchrittComponent {
  readonly personen = input<CustomerUserDTO[]>([]);
  readonly ichId = input<string | null>(null);
  readonly ichName = input('');
  readonly leadId = input<string | null>(null);
  readonly team = model<TeamZeile[]>([]);

  readonly rollen = PROJEKT_ROLLEN;

  private readonly waehlbar = computed(() =>
    this.personen().filter((p) => p.id !== this.ichId() && p.id !== this.leadId()),
  );

  readonly frei = computed(() => {
    const gewaehlt = new Set(this.team().map((z) => z.personId));
    return this.waehlbar().filter((p) => !gewaehlt.has(p.id));
  });

  readonly optionenJeZeile = computed<PersonOption[][]>(() => {
    const frei = new Set(this.frei().map((p) => p.id));
    return this.team().map((zeile) =>
      this.waehlbar()
        .filter((p) => p.id === zeile.personId || frei.has(p.id))
        .map((p) => ({ id: p.id, displayName: `${p.displayName} · ${p.email}` })),
    );
  });

  name(personId: string): string {
    return this.personen().find((p) => p.id === personId)?.displayName ?? '';
  }

  hinzufuegen(): void {
    const person = this.frei()[0];
    if (person) this.team.update((t) => [...t, { personId: person.id, rolle: 'PROJECT_WRITER' }]);
  }

  aendern(index: number, aenderung: Partial<TeamZeile>): void {
    this.team.update((t) => t.map((z, i) => (i === index ? { ...z, ...aenderung } : z)));
  }

  entfernen(index: number): void {
    this.team.update((t) => t.filter((_, i) => i !== index));
  }
}
